import { useMemo } from "react";

import clsx from "clsx";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import {
  useBreakdownDeactivatedCheck,
  useOverviewContext,
} from "@features/overview-v2/hooks";
import {
  TileType,
  gridIsReady,
  type TileDescriptor,
} from "@features/overview-v2/types";
import {
  type GetOverviewMetricsReport_me_overviewMetricsReport,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";

import { BreakdownsDeactivatedMessage } from "./breakdowns-deactivated-message";
import { BreakdownsNotSupportedMessage } from "./breakdowns-not-supported-message";
import { Chart } from "./chart";
import { FilterPills } from "./filter-pills";
import { Legend } from "./legend";
import { MetricValue } from "./metric-value";
import { SubText } from "./subtext";
import { TileHeader } from "./tile-header";
import { LEVEL_TO_NUMBER } from "./utils";

interface Props {
  tile: TileDescriptor;
  isResizing: boolean;
  metric1: SalesPageMetric;
  metric2?: SalesPageMetric;
  metric1Data: GetOverviewMetricsReport_me_overviewMetricsReport;
  metric1CompareData?: GetOverviewMetricsReport_me_overviewMetricsReport;
  metric2Data?: GetOverviewMetricsReport_me_overviewMetricsReport;
  metric2CompareData?: GetOverviewMetricsReport_me_overviewMetricsReport;
  onAboutToDelete(): void;
}

export const Content = ({
  tile,
  isResizing,
  metric1,
  metric2,
  metric1Data,
  metric1CompareData,
  metric2Data,
  metric2CompareData,
  onAboutToDelete,
}: Props) => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const {
    grid,
    labelsByLevel,
    filters: { level },
  } = useOverviewContext();

  const { width: tileWidth, height: tileHeight } = gridIsReady(grid)
    ? tile.layouts[grid.breakpoint]
    : { width: 0, height: 0 };

  const isBreakdownDeactivated = useBreakdownDeactivatedCheck(
    tile.breakdowns,
    tile.filters,
    breakdownConfigs,
  );

  const maxAllowedLevel = useMemo(() => {
    let result = 3;

    const getLevelNumber = (key: string, value: string) => {
      const lvl = labelsByLevel[`${key}:${value}`] ?? labelsByLevel[`${key}:*`];
      return LEVEL_TO_NUMBER[lvl];
    };

    tile.filters.forEach((filter) => {
      const lvlNumber = getLevelNumber(filter.breakdown, filter.value);

      if (lvlNumber < result) {
        result = lvlNumber;
      }
    });

    if (tile.metric2Filters) {
      tile.metric2Filters.forEach((filter) => {
        const lvlNumber = getLevelNumber(filter.breakdown, filter.value);

        if (lvlNumber < result) {
          result = lvlNumber;
        }
      });
    }

    tile.breakdownFilters.forEach((labelValues) => {
      labelValues.forEach((labelValue, idx) => {
        const lvlNumber = getLevelNumber(tile.breakdowns[idx], labelValue);

        if (lvlNumber < result) {
          result = lvlNumber;
        }
      });
    });

    return result;
  }, [labelsByLevel, tile]);

  const isUsingNotAllowedLevel = LEVEL_TO_NUMBER[level] > maxAllowedLevel;

  const isTwoMetricTile = metric2 && metric2Data;
  const hasSecondMetricFilters =
    isTwoMetricTile && tile.metric2Filters && tile.metric2Filters.length > 0;

  const showTopTwoMetric =
    isTwoMetricTile && tile.filters.length > 0 && tile.type !== TileType.METRIC;

  const hasError = isBreakdownDeactivated || isUsingNotAllowedLevel;

  return (
    <div className="flex flex-col h-full gap-2 overflow-hidden">
      <div className="flex flex-col">
        <TileHeader
          tile={tile}
          metric1={metric1}
          metric2={metric2}
          metric1Data={metric1Data}
          metric2Data={metric2Data}
          variant={isTwoMetricTile ? "multi" : "single"}
          onDelete={onAboutToDelete}
          className="px-6"
        />
        {!hasError && (
          <SubText metric1={metric1} metric2={metric2} tile={tile} />
        )}
      </div>
      <div className="flex flex-col grow overflow-hidden">
        {tile.type !== TileType.CONVERSION_LAG && !hasError && (
          <>
            {tile.breakdowns.length === 0 && (
              <div className="flex flex-wrap justify-between gap-[20px] px-6">
                <div className="min-w-[calc(50%-10px)]">
                  <MetricValue
                    variant={isTwoMetricTile ? "multi" : "single"}
                    tileType={tile.type}
                    metric={metric1}
                    metricIndex={0}
                    data={metric1Data}
                    compareData={metric1CompareData}
                    filters={tile.filters}
                  />
                </div>
                {isTwoMetricTile && (
                  <div className="min-w-[calc(50%-10px)]">
                    <MetricValue
                      variant="multi"
                      tileType={tile.type}
                      metric={metric2}
                      metricIndex={1}
                      data={metric2Data}
                      compareData={metric2CompareData}
                      filters={tile.filters}
                    />
                  </div>
                )}
              </div>
            )}
            {tile.breakdowns.length > 0 && tile.type !== TileType.METRIC && (
              <div className="text-sm text-v2-secondary font-light px-6">
                Displaying {metric1Data.rows.length}{" "}
                {metric1Data.rows.length === 1 ? "category" : "categories"}
              </div>
            )}
          </>
        )}
        {showTopTwoMetric && !hasError && (
          <div className="flex flex-wrap justify-between gap-[20px] px-6">
            <div className="flex-1">
              <FilterPills
                tileType={tile.type}
                filters={tile.filters}
                isResizing={isResizing}
                tileHeight={tileHeight}
              />
            </div>
            {hasSecondMetricFilters && (
              <div className="flex-1">
                <FilterPills
                  tileType={tile.type}
                  filters={tile.metric2Filters ?? []}
                  isResizing={isResizing}
                  tileHeight={tileHeight}
                />
              </div>
            )}
          </div>
        )}
        <div
          className={clsx("flex-1 flex overflow-hidden", {
            "flex-col": tileWidth === 1,
            "flex-row": tileWidth !== 1,
          })}
        >
          {tile.type !== TileType.METRIC && (
            <div
              className={clsx("flex-1 pt-1 overflow-hidden", {
                "ml-3 mr-5": !hasError,
                "mb-2": hasError,
              })}
            >
              {isBreakdownDeactivated && (
                <div className="h-full bg-mars-2 flex items-center justify-center mx-6 px-4">
                  <BreakdownsDeactivatedMessage />
                </div>
              )}
              {isUsingNotAllowedLevel && !isBreakdownDeactivated && (
                <div className="h-full bg-mars-2 flex items-center justify-center mx-6 px-4">
                  <BreakdownsNotSupportedMessage
                    allwedLevel={maxAllowedLevel}
                  />
                </div>
              )}
              {!hasError && (
                <Chart
                  tile={tile}
                  metric1={metric1}
                  metric2={metric2}
                  metric1Data={metric1Data}
                  metric2Data={metric2Data}
                />
              )}
            </div>
          )}
          {tile.type === TileType.METRIC && isBreakdownDeactivated && (
            <div className="h-full bg-mars-2 flex items-center justify-center mx-6 px-4 mb-2">
              <BreakdownsDeactivatedMessage />
            </div>
          )}
          {tile.type === TileType.METRIC &&
            isUsingNotAllowedLevel &&
            !isBreakdownDeactivated && (
              <div className="h-full bg-mars-2 flex items-center justify-center mx-6 px-4">
                <BreakdownsNotSupportedMessage allwedLevel={maxAllowedLevel} />
              </div>
            )}
          {tile.breakdowns.length > 0 && !hasError && (
            <div className={clsx("pr-6", { "pl-6": tileWidth === 1 })}>
              <Legend
                tile={tile}
                tileWidth={tileWidth}
                tileHeight={tileHeight}
                metric={metric1}
                data={metric1Data}
                compareData={metric1CompareData}
              />
            </div>
          )}
        </div>
        {!showTopTwoMetric && !hasError && (
          <div className="flex flex-wrap justify-between gap-[20px] px-6">
            <div className="flex-1">
              {isTwoMetricTile && tile.filters.length > 0 && (
                <div className="text-xxs text-gray mb-1">
                  {metric1.metadata.overviewPageMetricName || metric1.name}{" "}
                  filters
                </div>
              )}
              <FilterPills
                tileType={tile.type}
                filters={tile.filters}
                isResizing={isResizing}
                tileHeight={tileHeight}
              />
            </div>
            {hasSecondMetricFilters && (
              <div className="flex-1">
                <div className="text-xxs text-gray mb-1">
                  {metric2.metadata.overviewPageMetricName || metric2.name}{" "}
                  filters
                </div>
                <FilterPills
                  tileType={tile.type}
                  filters={tile.metric2Filters ?? []}
                  isResizing={isResizing}
                  tileHeight={tileHeight}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
